/**
 * The shapes a generated picture may be asked for.
 *
 * The slot used to decide this outright - a cover was always 16:9 and an inline
 * picture always 1:1 - which is right as a default and wrong as a rule. A square
 * is a poor shape for a step-by-step photo beside a numbered list, and a clinic
 * publishing to a template with a tall hero has no way to say so. The writer can
 * see the page; the slot cannot.
 *
 * EVERY PICTURE HERE GOES INTO THE ARTICLE. That is the whole scope of this
 * screen, and the first version of this list forgot it: it offered 9:16 「SNS向け
 * の縦画像」, which is a shape for a platform this product does not publish to.
 * A social crop dropped into a column of body text is a picture the reader has
 * to scroll past. So the four presets are named by WHERE IN THE ARTICLE they sit
 * - the hint is placement advice, not a format label.
 *
 * The rest of the provider's ratios stay reachable under 「その他」 rather than
 * being hidden: a clinic whose theme wants 21:9 banners is not wrong, they are
 * just not the common case. What is NOT offered is a free-text ratio. Every
 * entry below is one the models actually accept; an arbitrary one is either
 * rejected outright or silently rounded to the nearest supported shape, and a
 * silently rounded picture is a bill for something nobody asked for.
 */

export interface ImageSize {
  /** As the provider spells it. */
  ratio: string;
  label: string;
  /** Where in an article this shape belongs. Empty for the uncommon ratios. */
  hint: string;
}

/**
 * The four an article actually uses, in the order a writer meets them.
 *
 * Ordered by how often they are the right answer rather than by geometry: the
 * square is the default inline picture, and the wide one is the cover.
 */
export const ARTICLE_SIZES: ImageSize[] = [
  { ratio: '1:1', label: '正方形', hint: '本文の途中。文章の流れを止めない' },
  { ratio: '4:3', label: '横', hint: '説明写真。段落の幅にそのまま収まる' },
  { ratio: '16:9', label: '横長', hint: 'カバー、節のとびら' },
  { ratio: '3:4', label: '縦', hint: '人物・施術室。スマートフォンで大きく見える' },
];

/**
 * Everything else the image models accept.
 *
 * Offered without advice, because there is none to give for an article: these
 * exist for a clinic whose own template asks for them. Kept to what the
 * providers support - see the note at the top on why this is not free text.
 */
export const OTHER_RATIOS = ['3:2', '2:3', '5:4', '4:5', '21:9', '9:16'];

export const IMAGE_RATIOS = [...ARTICLE_SIZES.map((size) => size.ratio), ...OTHER_RATIOS];

/**
 * What a slot asks for when nobody chooses.
 *
 * The old hard-coded behaviour, kept as the default: a cover is a wide banner
 * and an inline picture sits in a column of text. Changing what happens when
 * somebody makes no choice would change every article that never touches this
 * control.
 */
export const DEFAULT_RATIO = { cover: '16:9', inline: '1:1' } as const;

export const isImageRatio = (value: string): boolean => IMAGE_RATIOS.includes(value);
